import { Injectable } from '@angular/core';
import { CapitalAccountService } from '../capital-account/capital-account.service';
import { BsService } from '../bs/bs.service';
import { PlService } from '../pl/pl.service';
import { ExcelOptions, ExcelTemplateName } from 'src/app/domain/excel/interfaces';
import { PlExcelExportData } from 'src/app/domain/excel/templates/financial-statement/profit-and-loss/profitAndLossTemplate';
import { BsExcelExportData } from 'src/app/services/balance-sheet/bsTemplate';
import { FinancialStatement } from '../../models/common/FinancialStatement';
import { FinancialStatementAccount } from '../../models/common/FinancialStatementAccount';
import { FsExcelExportType, FsExportExcelData } from '../../utils/common/utilityClasses';
import { ExcelService } from 'src/app/domain/excel';
import { BehaviorSubject } from 'rxjs';
import { Fund } from 'src/app/domain/fund-performance/dto/Fund';
import { ManagementEntity } from 'src/app/domain/organization/dto/ManagementEntity';
import { FinancialStatementService } from './financial-statement.service';

@Injectable()
export class FsExportExcelService {
  private _ownershipEntities = new BehaviorSubject<(Fund | ManagementEntity)[]>([]);

  constructor(private capitalAccountService: CapitalAccountService,
    private bsService: BsService,
    private plService: PlService,
    private fsService: FinancialStatementService,
    private excelService: ExcelService) {
  }

  async exportExcel(excelTemplateName: FsExcelExportType,
    exportData?: FinancialStatementAccount | FinancialStatement | BsExcelExportData | PlExcelExportData): Promise<void> {
    if (!this.fsService.ownershipEntities || !this.fsService.selectedPeriod) {
      return;
    }
    let fileName = `${this.fsService.selectedPeriod.toString()}/${this.fsService.ownershipName}`;
    let data;
    let template: ExcelTemplateName;
    let options: ExcelOptions;
    switch (excelTemplateName) {

      case FsExcelExportType.FS:
        data = await this.exportAllExcels();
        template = ExcelTemplateName.financialStatement;
        fileName += '.xlsx';
        break;
      case FsExcelExportType.ASSEMBLY:
        data = this.fsService.financialStatement;
        template = ExcelTemplateName.assembly;
        fileName += '/assembly.xlsx';
        break;
      case FsExcelExportType.GL:
        data = this.fsService.financialStatement.gl;
        template = ExcelTemplateName.gl;
        fileName += '/gl.xlsx';
        break;
      case FsExcelExportType.TB:
        data = this.fsService.financialStatement;
        template = ExcelTemplateName.tb;
        fileName += '/tb.xlsx';
        break;
      case FsExcelExportType.MANUAL_ENTRIES:
        data = this.fsService.financialStatement.gl.filter(
          (transaction) => transaction.isManualEntry,
        );
        template = ExcelTemplateName.gl;
        fileName += '/manualEntries.xlsx';
        break;
      case FsExcelExportType.MANUAL_ACCOUNTS:
        data = this.fsService.financialStatement.makeCopy();
        data.tb = data.tb.filter((account: FinancialStatementAccount) => account.isManual);
        data.gl = null;
        template = ExcelTemplateName.tb;
        fileName += '/manualAccounts.xlsx';
        break;
      case FsExcelExportType.MANUAL_ADJUSTMENTS:
        const tempFsAdj = this.fsService.financialStatement.makeCopy();
        tempFsAdj.tb = tempFsAdj.tb.filter((account) => account.isManual);
        tempFsAdj.gl = tempFsAdj.gl.filter(
          (transaction) => transaction.isManualEntry,
        );
        data = tempFsAdj;
        template = ExcelTemplateName.tb;
        fileName += '/adjustments.xlsx';
        break;
      case FsExcelExportType.BS:
        if (exportData) {
          fileName = fileName + '/balanceSheet.xlsx';
          template = ExcelTemplateName.balanceSheetTemplate;
          data = exportData;
        }
        break;
      case FsExcelExportType.PL:
        if (exportData) {
          fileName = fileName + '/profitAndLoss.xlsx';
          data = exportData;
          template = ExcelTemplateName.profitAndLossTemplate;
        }
        break;
      case FsExcelExportType.QUARTERLY_GL:
        if (exportData && exportData instanceof FinancialStatementAccount) {
          data = exportData.quarterlyTransactionsMap;
          fileName += `/${exportData.code}/gl.xlsx`;
        } else {
          data = this.fsService.financialStatement.tb.map((acc) => acc.quarterlyTransactionsMap);
          fileName += '/gl.xlsx';
        }
        template = ExcelTemplateName.gl;
        options = { isQuarterlyGl: true };
        break;
    }
    this.excelService.exportByTemplate(data, fileName, template, options);
  }

  async exportAllExcels(): Promise<FsExportExcelData> {
    const capitalAccount = await this.capitalAccountService.initCapitalAccountData(this.fsService.financialStatement);
    const dataObject: FsExportExcelData = {
      fs: this.fsService.financialStatement,
      capitalAccountData: capitalAccount,
      plPageData: {
        shownPeriodsData:
          this.plService.initPlDateColumns(this.fsService.selectedPeriod, this.fsService.financialStatement.profitLossParts),
        parts: this.fsService.financialStatement.profitLossParts,
        selectedPeriod: this.fsService.financialStatement.period,
        periodType: 'Year over year',
      },

      bsPageData: {
        selectedBasedDataColumns: this.bsService.initBsPageData(this.fsService.financialStatement).selectedBSDateColumns,
        parts: this.fsService.financialStatement.balanceSheetParts,
        selectedPeriod: this.fsService.financialStatement.period,
      },
    };
    return dataObject;
  };
};
